import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";
import { AuthProvider, useAuth } from "@/lib/auth";
import { ThemeProvider, useTheme } from "@/lib/theme";
import LiquidGlassFilter from "@/components/LiquidGlassFilter";
import LaunchLock from "@/components/LaunchLock";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import Order from "@/pages/Order";
import Dashboard from "@/pages/Dashboard";
import OrderDetail from "@/pages/OrderDetail";
import Affiliate from "@/pages/Affiliate";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import PopupCallback from "@/pages/PopupCallback";
import Contact from "@/pages/Contact";
import Prime from "@/pages/Prime";
import Settings from "@/pages/Settings";

function Protected({ children, admin = false }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="squircle border border-[#2b2b2b] bg-[#111] px-8 py-6 text-sm text-[#a0a0a0]">
          Loading…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (admin && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
}

function HomeRoute() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <div className="squircle border border-[#2b2b2b] bg-[#111] px-8 py-6 text-sm text-[#a0a0a0]">
          Loading…
        </div>
      </div>
    );
  }
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return <Landing />;
}

function AppRouter() {
  const location = useLocation();
  // Popup OAuth callback owns its own session_id handling (postMessage + window.close).
  // Never let the full-page AuthCallback hijack it.
  if (location.pathname === "/auth/popup-callback") {
    return <PopupCallback />;
  }
  // Synchronous session_id detection (Emergent OAuth full-page flow)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<HomeRoute />} />
      <Route path="/auth/popup-callback" element={<PopupCallback />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/prime" element={<Prime />} />
      <Route path="/settings" element={<Settings />} />
      <Route path="/order" element={<Order />} />
      <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
      <Route path="/dashboard/order/:orderId" element={<Protected><OrderDetail /></Protected>} />
      <Route path="/affiliate" element={<Protected><Affiliate /></Protected>} />
      <Route path="/admin" element={<Protected admin><Admin /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { theme } = useTheme();
  return (
    <Toaster
      theme={theme}
      position="top-right"
      toastOptions={{
        style: {
          background: theme === "dark" ? "#111" : "#ffffff",
          border: theme === "dark" ? "1px solid #2b2b2b" : "1px solid #e5e5e5",
          color: theme === "dark" ? "#fff" : "#0a0a0a",
          borderRadius: "18px",
        },
      }}
    />
  );
}

export default function App() {
  return (
    <div className="App">
      <LaunchLock>
        <ThemeProvider>
          <LiquidGlassFilter />
          <BrowserRouter>
            <AuthProvider>
              <AppRouter />
              <ThemedToaster />
            </AuthProvider>
          </BrowserRouter>
        </ThemeProvider>
      </LaunchLock>
    </div>
  );
}
